import Disclaimer from '@components/common/Disclaimer';
import { media } from '@styles/mediaQuery';
import theme from '@theme/index';
import styled, { css } from 'styled-components';

const Transition = css`
  transition: right 0.25s ease-in-out;
`;

/**
 * 필터 영역
 * @param pinned 필터 고정 여부
 * @param enabled 필터 활성화 여부
 * @param filterWidth 커스텀 필터 너비
 */
export const FilterArea = styled.form<{
  pinned: boolean;
  enabled: boolean;
  fixed?: boolean;
  filterWidth?: string;
}>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  width: ${({ filterWidth }) => filterWidth || '20rem'};
  background: ${theme.white};
  border-left: 1px solid ${theme.neutralGray[200]};

  ${({ fixed }) =>
    fixed &&
    css`
      /* 데스크톱: fixed 포지션으로 항상 고정 */
      position: fixed;
      right: 0;
      top: 4rem;
      height: calc(100dvh - 4rem);
    `}

  ${({ fixed, pinned, enabled }) =>
    !fixed &&
    pinned &&
    css`
      /* 핀 고정 상태: enabled 여부에 따라 표시/숨김 */
      position: fixed;
      right: ${enabled ? '0' : '-100%'};
      top: 4rem;
      height: calc(100dvh - 4rem);
      z-index: 20;
      ${Transition}
    `}

  ${({ fixed, pinned, enabled }) =>
    !fixed &&
    !pinned &&
    css`
      /* 모바일: 슬라이드 인/아웃 */
      position: fixed;
      right: ${enabled ? '0rem' : '-100%'};
      top: 0;
      height: 100dvh;
      z-index: 10;
      ${Transition}
    `}

  /* 모바일에서는 pinned 상태와 관계없이 전체 높이 사용 */
  ${media.mobile`
    width: 100%;
    top: 0;
    height: 100dvh;
    z-index: 10;
  `}
`;

/**
 * FilterTitleArea - 필터 타이틀 영역
 * 좌측(핀 버튼) - 중앙(타이틀) - 우측(닫기 버튼)
 */
export const FilterTitleArea = styled.div`
  display: grid;
  grid-template-columns: 2rem auto 2rem;
  align-items: center;
  justify-items: center;
  height: 4rem;
  padding: 0 1rem;
  span {
    font-size: 1.5rem;
    font-weight: 600;
  }
  img {
    width: 1.5rem;
  }
`;

/**
 * FilterContentArea - 필터 컨텐츠 영역
 * 스크롤 가능한 필터 아이템 목록
 */
export const FilterContentArea = styled.div`
  width: 100%;
  flex-grow: 1;
  overflow-y: auto;

  border-top: 1px solid ${theme.neutralGray[200]};
  border-bottom: 1px solid ${theme.neutralGray[200]};

  > div + div {
    border-top: 0.5rem solid ${theme.neutralGray[100]};
  }
`;

/**
 * FilterApplyArea - 필터 적용/초기화 버튼 영역
 */
export const FilterApplyArea = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  padding: 1rem;
`;

/**
 * FilterItemContainer - 개별 필터 아이템 컨테이너
 *
 * @param isFlex - true일 경우 가로 배치 (라디오 버튼 등)
 */
export const FilterItemContainer = styled.div<{ isFlex?: boolean }>`
  width: ${({ isFlex }) => (isFlex ? 'auto' : '100%')};
  display: flex;
  flex-direction: column;
  padding: 1rem;
  gap: 1rem;

  ${({ isFlex }) =>
    isFlex &&
    css`
      flex-direction: row;
      align-items: center;
    `}

  h3 {
    font-size: 1rem;
    font-weight: 500;
    margin: 0;
  }
`;

/**
 * BoxItemList - 체크박스/라디오 아이템 리스트
 */
export const BoxItemList = styled.div<{ isFlex?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0 0.25rem;
  gap: 1rem;

  ${({ isFlex }) =>
    isFlex &&
    css`
      flex-direction: row;
    `}
`;

/**
 * BoxItem - 체크박스/라디오 개별 아이템
 */
export const BoxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${theme.neutralGray[400]};
`;

/**
 * FilterContentPlacer - 2열 그리드 레이아웃
 */
export const FilterContentPlacer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
`;

/**
 * DatePickerDateRangeWrapper - 날짜 범위 선택 래퍼
 */
export const DatePickerDateRangeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .date-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    background: ${theme.neutralGray[100]};
    border-radius: 0.5rem;
    border: 1px solid ${theme.neutralGray[200]};

    .date-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      .date-label {
        font-size: 0.75rem;
        font-weight: 500;
        color: ${theme.neutralGray[500]};
        min-width: 2.5rem;
        flex-shrink: 0;
      }

      input {
        flex: 1;
        margin: 0;
        font-size: 0.8125rem;
      }
    }
  }
`;

/**
 * FlexRow - 가로 배치용 래퍼
 */
const FlexRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

/**
 * FilterItem - 필터 아이템 컴포넌트
 *
 * @param title - 필터 타이틀
 * @param content - 필터 컨텐츠 (JSX)
 * @param isFlex - 가로 배치 여부
 * @param description - 필터 설명
 */
export const FilterItem = ({
  title,
  content,
  isFlex,
  description,
}: {
  title: string;
  content: any;
  isFlex?: boolean;
  description?: string;
}) => {
  // isFlex이고 설명이 있는 경우: 헤더(타이틀+컨텐츠)는 가로 배치, 설명은 하단 배치
  if (isFlex && description) {
    return (
      <FilterItemContainer isFlex={false}>
        <FlexRow>
          {title && <h3>{title}</h3>}
          {content}
        </FlexRow>
        <Disclaimer message={description} dense type="info" />
      </FilterItemContainer>
    );
  }

  return (
    <FilterItemContainer isFlex={isFlex}>
      {title && <h3>{title}</h3>}
      {description && <Disclaimer message={description} dense type="info" />}
      {content}
    </FilterItemContainer>
  );
};

/**
 * ResetButton - 초기화 버튼
 */
export const ResetButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.neutralGray[300]};
  border-radius: 0.25rem;
  background: ${theme.white};
  color: ${theme.neutralGray[600]};
  font-weight: 500;

  &:disabled {
    opacity: 0.5;
  }
`;

/**
 * ApplyButton - 적용 버튼
 */
export const ApplyButton = styled.button.attrs({ type: 'submit' })`
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 0.25rem;
  background: ${theme.primary};
  color: ${theme.white};
  font-weight: 600;

  &:disabled {
    opacity: 0.5;
  }
`;
